import React, { useState } from "react";
import { Row, Col, Card, CardBody, Nav, NavItem, NavLink, TabContent, TabPane, Badge, Button } from "reactstrap";

const ClientDetailView = ({ client, onBack, onEdit, onStatusChange }) => {
  const [activeTab, setActiveTab] = useState("1");

  if (!client) {
    return (
      <div className="text-center py-5">
        <p className="text-muted">No client data selected.</p>
        <button className="btn btn-primary" onClick={onBack}>
          <i className="bx bx-arrow-back me-1" /> Back to Client List
        </button>
      </div>
    );
  }

  const raw = client.raw || {};
  const isEntity = client.id?.startsWith("e_") || Boolean(raw.entity_title);

  // Address parsing
  const formattedAddress = [
    raw.address || raw.address_line1,
    raw.address_line2,
    raw.city,
    raw.state,
    raw.zip_code || raw.zip,
    raw.country
  ].filter(Boolean).join(", ") || "No address registered";

  const statusColor = client.status === "Active" ? "success" : "warning";
  const initials = (client.full_name || "C").charAt(0).toUpperCase();

  return (
    <div className="client-detail-view animate__animated animate__fadeIn">
      {/* Top Breadcrumb & Navigation Bar */}
      <div className="d-flex align-items-center justify-content-between mb-4 pb-3 border-bottom">
        <div className="d-flex align-items-center gap-3">
          <button
            type="button"
            className="btn btn-light btn-sm rounded-circle d-flex align-items-center justify-content-center shadow-sm"
            style={{ width: "38px", height: "38px" }}
            onClick={onBack}
            title="Back to Client List"
          >
            <i className="bx bx-arrow-back fs-5 text-dark" />
          </button>
          <div>
            <div className="d-flex align-items-center gap-2">
              <span className="text-muted small" style={{ cursor: "pointer" }} onClick={onBack}>
                Client Management
              </span>
              <span className="text-muted small">/</span>
              <span className="text-dark small fw-semibold">{client.full_name}</span>
            </div>
            <h4 className="fw-bold text-dark mb-0">{client.full_name}</h4>
          </div>
        </div>

        <div className="d-flex align-items-center gap-2">
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm px-3 rounded-pill fw-semibold"
            onClick={() => window.print()}
          >
            <i className="bx bx-printer me-1" /> Print Dossier
          </button>
          <button
            type="button"
            className="btn btn-primary btn-sm px-3 rounded-pill fw-semibold"
            onClick={() => onEdit && onEdit(client)}
          >
            <i className="bx bx-pencil me-1" /> Edit Client Details
          </button>
        </div>
      </div>

      {/* Hero Profile Overview Card */}
      <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: "16px", overflow: "hidden" }}>
        <div style={{ background: "linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)", height: "110px", position: "relative" }} />
        <CardBody className="pt-0 position-relative">
          <div className="d-flex flex-wrap align-items-end justify-content-between gap-3" style={{ marginTop: "-50px" }}>
            <div className="d-flex align-items-end gap-3">
              <div
                className="rounded-4 bg-white p-1 shadow-sm d-flex align-items-center justify-content-center"
                style={{ width: "100px", height: "100px", zIndex: 2 }}
              >
                <div
                  className="rounded-3 w-100 h-100 d-flex align-items-center justify-content-center text-white fw-bold fs-2"
                  style={{ background: "#2563eb" }}
                >
                  {initials}
                </div>
              </div>

              <div className="mb-2">
                <div className="d-flex align-items-center gap-2">
                  <h4 className="fw-bold text-dark mb-0">{client.full_name}</h4>
                  <Badge color={statusColor} pill className="px-2 py-1">
                    {client.status}
                  </Badge>
                  <Badge color="light" className="text-dark border px-2 py-1">
                    {client.type}
                  </Badge>
                </div>
                <p className="text-muted small mb-0 mt-1">
                  <i className="bx bx-envelope me-1" /> {client.email} &nbsp;•&nbsp;
                  <i className="bx bx-phone me-1 ms-1" /> {client.phone || raw.contact_number || "No Phone"} &nbsp;•&nbsp;
                  <i className="bx bx-id-card me-1 ms-1" /> ID: {client.id}
                </p>
              </div>
            </div>

            <div className="d-flex align-items-center gap-2 mb-2">
              <div className="text-end me-3 d-none d-md-block">
                <small className="text-muted d-block">Account Status</small>
                <span className={`fw-bold text-${statusColor}`}>
                  {client.status === "Active" ? "● Fully Operational" : "○ Pending Verification"}
                </span>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* KPI Metric Summary Cards */}
      <Row className="g-3 mb-4">
        <Col md={3} sm={6}>
          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: "12px" }}>
            <CardBody className="p-3">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <small className="text-muted fw-semibold">Total Deals Processed</small>
                  <h4 className="fw-bold text-dark mb-0 mt-1">14 Deals</h4>
                  <small className="text-success fw-semibold"><i className="bx bx-trending-up" /> 3 Active Now</small>
                </div>
                <div className="rounded-3 p-3 bg-primary-subtle text-primary fs-3">
                  <i className="bx bx-briefcase-alt-2" />
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>

        <Col md={3} sm={6}>
          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: "12px" }}>
            <CardBody className="p-3">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <small className="text-muted fw-semibold">Team Members / Agents</small>
                  <h4 className="fw-bold text-dark mb-0 mt-1">8 Seats</h4>
                  <small className="text-muted">6 Active Coordinators</small>
                </div>
                <div className="rounded-3 p-3 bg-success-subtle text-success fs-3">
                  <i className="bx bx-group" />
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>

        <Col md={3} sm={6}>
          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: "12px" }}>
            <CardBody className="p-3">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <small className="text-muted fw-semibold">Subscription Plan</small>
                  <h4 className="fw-bold text-primary mb-0 mt-1">Enterprise Pro</h4>
                  <small className="text-muted">Renews Oct 2026</small>
                </div>
                <div className="rounded-3 p-3 bg-warning-subtle text-warning fs-3">
                  <i className="bx bx-crown" />
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>

        <Col md={3} sm={6}>
          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: "12px" }}>
            <CardBody className="p-3">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <small className="text-muted fw-semibold">Total Transaction Volume</small>
                  <h4 className="fw-bold text-dark mb-0 mt-1">$4.85M</h4>
                  <small className="text-success fw-semibold"><i className="bx bx-check-circle" /> 100% Compliant</small>
                </div>
                <div className="rounded-3 p-3 bg-info-subtle text-info fs-3">
                  <i className="bx bx-dollar-circle" />
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Main Tabbed Details View */}
      <Card className="border-0 shadow-sm" style={{ borderRadius: "16px" }}>
        <CardBody className="p-4">
          <Nav tabs className="nav-tabs-custom mb-4 border-bottom">
            <NavItem>
              <NavLink
                style={{ cursor: "pointer" }}
                className={activeTab === "1" ? "active fw-bold text-primary" : "text-muted"}
                onClick={() => setActiveTab("1")}
              >
                <i className="bx bx-id-card me-1" /> Profile & Identity
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                style={{ cursor: "pointer" }}
                className={activeTab === "2" ? "active fw-bold text-primary" : "text-muted"}
                onClick={() => setActiveTab("2")}
              >
                <i className="bx bx-group me-1" /> Team Members & Contacts
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                style={{ cursor: "pointer" }}
                className={activeTab === "3" ? "active fw-bold text-primary" : "text-muted"}
                onClick={() => setActiveTab("3")}
              >
                <i className="bx bx-folder-open me-1" /> Deals & Transactions
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                style={{ cursor: "pointer" }}
                className={activeTab === "4" ? "active fw-bold text-primary" : "text-muted"}
                onClick={() => setActiveTab("4")}
              >
                <i className="bx bx-credit-card me-1" /> Subscriptions & Billing
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                style={{ cursor: "pointer" }}
                className={activeTab === "5" ? "active fw-bold text-primary" : "text-muted"}
                onClick={() => setActiveTab("5")}
              >
                <i className="bx bx-shield-quarter me-1" /> Security & Audit Log
              </NavLink>
            </NavItem>
          </Nav>

          <TabContent activeTab={activeTab}>
            {/* Tab 1: Profile & Identity */}
            <TabPane tabId="1">
              <Row className="g-4">
                <Col lg={6}>
                  <h6 className="fw-bold text-dark mb-3 pb-2 border-bottom">
                    <i className="bx bx-building-house me-2 text-primary" />Entity & Contact Information
                  </h6>
                  <div className="d-flex flex-column gap-3">
                    <div className="d-flex justify-content-between p-3 bg-light rounded-3">
                      <span className="text-muted">Legal Title / Company</span>
                      <strong className="text-dark">{raw.entity_title || raw.company_name || client.full_name}</strong>
                    </div>
                    <div className="d-flex justify-content-between p-3 bg-light rounded-3">
                      <span className="text-muted">Primary Contact Person</span>
                      <strong className="text-dark">{raw.contact_person || client.full_name}</strong>
                    </div>
                    <div className="d-flex justify-content-between p-3 bg-light rounded-3">
                      <span className="text-muted">Official Email Address</span>
                      <strong className="text-dark">{client.email}</strong>
                    </div>
                    <div className="d-flex justify-content-between p-3 bg-light rounded-3">
                      <span className="text-muted">Direct Phone Line</span>
                      <strong className="text-dark">{client.phone || raw.contact_number || "—"}</strong>
                    </div>
                    <div className="d-flex justify-content-between p-3 bg-light rounded-3">
                      <span className="text-muted">Brokerage License #</span>
                      <strong className="text-dark">{raw.entity_licence || raw.license_number || "FL-BK349012"}</strong>
                    </div>
                    <div className="d-flex justify-content-between p-3 bg-light rounded-3">
                      <span className="text-muted">Team / Brand</span>
                      <strong className="text-dark">{raw.team_name || "Independent"}</strong>
                    </div>
                  </div>
                </Col>

                <Col lg={6}>
                  <h6 className="fw-bold text-dark mb-3 pb-2 border-bottom">
                    <i className="bx bx-map-pin me-2 text-primary" />Registered Address & Web Presence
                  </h6>
                  <div className="d-flex flex-column gap-3">
                    <div className="p-3 bg-light rounded-3">
                      <span className="text-muted d-block small mb-1">Full Physical Office Address</span>
                      <p className="fw-semibold text-dark mb-0">
                        <i className="bx bx-map me-1 text-danger" />
                        {formattedAddress}
                      </p>
                    </div>

                    <div className="d-flex justify-content-between p-3 bg-light rounded-3">
                      <span className="text-muted">City / State</span>
                      <strong className="text-dark">{raw.city || "Miami"}, {raw.state || "FL"}</strong>
                    </div>

                    <div className="d-flex justify-content-between p-3 bg-light rounded-3">
                      <span className="text-muted">Postal / ZIP Code</span>
                      <strong className="text-dark">{raw.zip_code || raw.zip || "33101"}</strong>
                    </div>

                    <div className="d-flex justify-content-between p-3 bg-light rounded-3">
                      <span className="text-muted">Country</span>
                      <strong className="text-dark">{raw.country || "United States"}</strong>
                    </div>

                    <div className="d-flex justify-content-between p-3 bg-light rounded-3">
                      <span className="text-muted">Account Registered On</span>
                      <strong className="text-dark">
                        {raw.registered_on || raw.createdAt
                          ? new Date(raw.registered_on || raw.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
                          : "Jan 15, 2025"}
                      </strong>
                    </div>
                  </div>
                </Col>
              </Row>
            </TabPane>

            {/* Tab 2: Team Members & Contacts */}
            <TabPane tabId="2">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h6 className="fw-bold text-dark mb-0">Affiliated Agents & Team Members (4)</h6>
                <button type="button" className="btn btn-sm btn-outline-primary rounded-pill">
                  <i className="bx bx-user-plus me-1" /> Add Member
                </button>
              </div>

              <div className="table-responsive border rounded-3 bg-white">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Name</th>
                      <th>Role</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Status</th>
                      <th>Last Active</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <div className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center fw-bold" style={{ width: "32px", height: "32px", fontSize: "12px" }}>
                            {initials}
                          </div>
                          <div>
                            <strong className="d-block">{client.full_name}</strong>
                            <small className="text-muted">Owner</small>
                          </div>
                        </div>
                      </td>
                      <td><Badge color="primary">Lead Broker / Admin</Badge></td>
                      <td>{client.email}</td>
                      <td>{client.phone || "—"}</td>
                      <td><span className="badge bg-success-subtle text-success">Active</span></td>
                      <td>Just now</td>
                    </tr>
                    <tr>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <div className="rounded-circle bg-info text-white d-flex align-items-center justify-content-center fw-bold" style={{ width: "32px", height: "32px", fontSize: "12px" }}>
                            SK
                          </div>
                          <div>
                            <strong className="d-block">Sai Kamuni</strong>
                            <small className="text-muted">Coordinator</small>
                          </div>
                        </div>
                      </td>
                      <td><Badge color="info">Transaction Coordinator</Badge></td>
                      <td>sai.tc@dealtoclose.com</td>
                      <td>+1 (561) 400-8812</td>
                      <td><span className="badge bg-success-subtle text-success">Active</span></td>
                      <td>2 hours ago</td>
                    </tr>
                    <tr>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <div className="rounded-circle bg-secondary text-white d-flex align-items-center justify-content-center fw-bold" style={{ width: "32px", height: "32px", fontSize: "12px" }}>
                            AN
                          </div>
                          <div>
                            <strong className="d-block">Ashley Niemela</strong>
                            <small className="text-muted">Agent</small>
                          </div>
                        </div>
                      </td>
                      <td><Badge color="secondary">Licensed Realtor</Badge></td>
                      <td>ashley.realtor@gmail.com</td>
                      <td>+1 (561) 902-3341</td>
                      <td><span className="badge bg-success-subtle text-success">Active</span></td>
                      <td>Yesterday</td>
                    </tr>
                    <tr>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <div className="rounded-circle bg-warning text-white d-flex align-items-center justify-content-center fw-bold" style={{ width: "32px", height: "32px", fontSize: "12px" }}>
                            RS
                          </div>
                          <div>
                            <strong className="d-block">Robert Smith</strong>
                            <small className="text-muted">Title Officer</small>
                          </div>
                        </div>
                      </td>
                      <td><Badge color="warning" className="text-dark">Title & Escrow Partner</Badge></td>
                      <td>robert@floridatitle.com</td>
                      <td>+1 (305) 554-1290</td>
                      <td><span className="badge bg-success-subtle text-success">Active</span></td>
                      <td>3 days ago</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </TabPane>

            {/* Tab 3: Deals & Transactions */}
            <TabPane tabId="3">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h6 className="fw-bold text-dark mb-0">Recent Deals & Transactions</h6>
                <Badge color="primary" pill>14 Total Deals</Badge>
              </div>

              <div className="table-responsive border rounded-3 bg-white">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Property Address</th>
                      <th>Representation</th>
                      <th>Purchase Price</th>
                      <th>Closing Date</th>
                      <th>Compliance Status</th>
                      <th>Deal Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <strong>123 Primrose Lane, Miami FL 33101</strong>
                        <small className="text-muted d-block">FAR/BAR ASIS Residential</small>
                      </td>
                      <td><Badge color="info">Buyer Representation</Badge></td>
                      <td><strong>$530,000</strong></td>
                      <td>Sep 15, 2026</td>
                      <td><span className="badge bg-success">Approved (14/14)</span></td>
                      <td><span className="badge bg-warning text-dark">Under Contract</span></td>
                    </tr>
                    <tr>
                      <td>
                        <strong>456 Ocean Drive, Apt 4B, South Beach FL</strong>
                        <small className="text-muted d-block">Condominium Purchase</small>
                      </td>
                      <td><Badge color="primary">Seller Representation</Badge></td>
                      <td><strong>$1,250,000</strong></td>
                      <td>Aug 30, 2026</td>
                      <td><span className="badge bg-success">Approved (18/18)</span></td>
                      <td><span className="badge bg-success">Closed</span></td>
                    </tr>
                    <tr>
                      <td>
                        <strong>789 Sunset Boulevard, Fort Lauderdale FL</strong>
                        <small className="text-muted d-block">Single Family Residence</small>
                      </td>
                      <td><Badge color="secondary">Dual Agency</Badge></td>
                      <td><strong>$795,000</strong></td>
                      <td>Oct 05, 2026</td>
                      <td><span className="badge bg-warning text-dark">In Review (8/14)</span></td>
                      <td><span className="badge bg-primary">Active Listing</span></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </TabPane>

            {/* Tab 4: Subscriptions & Billing */}
            <TabPane tabId="4">
              <Row className="g-4">
                <Col lg={5}>
                  <Card className="border bg-light rounded-3 p-3">
                    <h6 className="fw-bold text-dark mb-3">Current Active Plan</h6>
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <span className="text-muted">Tier</span>
                      <strong className="text-primary fs-5">Enterprise Pro</strong>
                    </div>
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <span className="text-muted">Billing Cycle</span>
                      <strong className="text-dark">Annual ($2,400 / yr)</strong>
                    </div>
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <span className="text-muted">Payment Gateway</span>
                      <strong className="text-dark"><i className="bx bxl-stripe text-primary me-1" /> Stripe Auto-pay</strong>
                    </div>
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <span className="text-muted">Next Renewal</span>
                      <strong className="text-dark">Oct 15, 2026</strong>
                    </div>
                    <div className="d-flex align-items-center justify-content-between">
                      <span className="text-muted">Plan Status</span>
                      <span className="badge bg-success">Active & Paid</span>
                    </div>
                  </Card>
                </Col>

                <Col lg={7}>
                  <h6 className="fw-bold text-dark mb-3">Invoice & Payment History</h6>
                  <div className="table-responsive border rounded-3 bg-white">
                    <table className="table table-hover align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Invoice #</th>
                          <th>Date</th>
                          <th>Amount</th>
                          <th>Status</th>
                          <th>Receipt</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td><strong>INV-2026-001</strong></td>
                          <td>Oct 15, 2025</td>
                          <td>$2,400.00</td>
                          <td><span className="badge bg-success">Paid</span></td>
                          <td><button className="btn btn-sm btn-link text-primary p-0"><i className="bx bx-download me-1" />PDF</button></td>
                        </tr>
                        <tr>
                          <td><strong>INV-2025-098</strong></td>
                          <td>Oct 15, 2024</td>
                          <td>$2,400.00</td>
                          <td><span className="badge bg-success">Paid</span></td>
                          <td><button className="btn btn-sm btn-link text-primary p-0"><i className="bx bx-download me-1" />PDF</button></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </Col>
              </Row>
            </TabPane>

            {/* Tab 5: Security & Audit Log */}
            <TabPane tabId="5">
              <h6 className="fw-bold text-dark mb-3">Security & Account Audit Trail</h6>
              <div className="d-flex flex-column gap-2">
                <div className="d-flex align-items-center justify-content-between p-3 border rounded-3 bg-light">
                  <div className="d-flex align-items-center gap-3">
                    <div className="rounded-circle p-2 bg-success-subtle text-success fs-5">
                      <i className="bx bx-log-in-circle" />
                    </div>
                    <div>
                      <strong className="text-dark d-block">Successful Admin Login</strong>
                      <small className="text-muted">IP: 172.56.21.90 (Miami, FL) • Chrome on macOS</small>
                    </div>
                  </div>
                  <small className="text-muted">Today at 10:14 AM</small>
                </div>

                <div className="d-flex align-items-center justify-content-between p-3 border rounded-3 bg-light">
                  <div className="d-flex align-items-center gap-3">
                    <div className="rounded-circle p-2 bg-primary-subtle text-primary fs-5">
                      <i className="bx bx-file" />
                    </div>
                    <div>
                      <strong className="text-dark d-block">Created New Transaction: 123 Primrose Lane</strong>
                      <small className="text-muted">AI OCR processed contract successfully</small>
                    </div>
                  </div>
                  <small className="text-muted">Yesterday at 4:32 PM</small>
                </div>

                <div className="d-flex align-items-center justify-content-between p-3 border rounded-3 bg-light">
                  <div className="d-flex align-items-center gap-3">
                    <div className="rounded-circle p-2 bg-warning-subtle text-warning fs-5">
                      <i className="bx bx-key" />
                    </div>
                    <div>
                      <strong className="text-dark d-block">Security Credentials Updated</strong>
                      <small className="text-muted">Password changed by user</small>
                    </div>
                  </div>
                  <small className="text-muted">Aug 12, 2026</small>
                </div>
              </div>
            </TabPane>
          </TabContent>
        </CardBody>
      </Card>
    </div>
  );
};

export default ClientDetailView;
